import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, paymentsAPI, expensesAPI } from '../services/api';
import { ModernCard, ModernBadge, ModernLoadingSpinner, ModernStatCard, ModernInput, ModernButton, ModernModal, ModernTable } from '../components/ui/ModernComponents';
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';
import { formatCurrency } from '../utils/currency';
import { expenseCategoryLabel, expensePaymentLabel, todayInCasablanca } from '../constants/expenses';
import { formatDateOnlyFr } from '../utils/dateFormat';
import { Chart, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, LineElement } from 'chart.js';
import { Package, Users, ClipboardList, ShoppingCart, TrendingUp, BarChart3, PieChart, LineChart, Coins, AlertTriangle, DollarSign, Calendar, Activity, Target, CreditCard, Info, ListTodo, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
Chart.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, LineElement);

const getToday = todayInCasablanca;
const getCurrentMonthRange = () => {
  const today = getToday();
  const [year, month] = today.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const monthPrefix = today.slice(0, 7);
  return { start: `${monthPrefix}-01`, end: `${monthPrefix}-${lastDay}` };
};

const PAYMENT_MODE_CARDS = [
  { mode: 'espece', title: 'Espèces', variant: 'success', iconClass: 'text-green-600', totalKey: 'total_espece', countKey: 'count_espece' },
  { mode: 'cheque', title: 'Chèques', variant: 'primary', iconClass: 'text-blue-600', totalKey: 'total_cheque', countKey: 'count_cheque' },
  { mode: 'carte_bancaire', title: 'Cartes Bancaires', variant: 'warning', iconClass: 'text-purple-600', totalKey: 'total_carte', countKey: 'count_carte' },
  { mode: 'virement', title: 'Virements', variant: 'primary', iconClass: 'text-indigo-600', totalKey: 'total_virement', countKey: 'count_virement' },
];

const MODE_LABELS = {
  espece: 'Espèces',
  cheque: 'Chèque',
  carte_bancaire: 'Carte bancaire',
  virement: 'Virement',
};

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    keyStats: {
      products: { total_products: 0, total_stock: 0 },
      clients: { total_clients: 0 },
      orders: { total_orders: 0 },
      sales: { total_sales: 0, total_revenue: 0 },
      payments: { total_payments: 0, total_amount: 0, paid_count: 0, partial_count: 0, unpaid_count: 0 }
    },
    charts: {
      salesTrends: [],
      topProducts: [],
      clientGrowth: [],
      stockByCategory: [],
      creditsStats: [],
      revenueByStore: [],
      employeePerformance: [],
      paymentMethods: [],
      paymentTypes: [],
      clientReferrals: []
    },
    notifications: {
      overduePayments: []
    }
  });
  const [dateRange, setDateRange] = useState(getCurrentMonthRange);
  const [error, setError] = useState(null);
  const dashboardRequestId = useRef(0);
  const expenseRequestId = useRef(0);
  const [expenseDetails, setExpenseDetails] = useState({ open: false, loading: false, rows: [], summary: null, page: 1, pageSize: 25, range: {}, error: null });
  const openExpenseDetails = async (page = 1, range = dateRange) => {
    const requestId = ++expenseRequestId.current;
    setExpenseDetails({ open: true, loading: true, rows: [], summary: null, page, pageSize: 25, range: { ...range }, error: null });
    try {
      const response = await expensesAPI.list({ start_date: range.start, end_date: range.end, page });
      if (requestId === expenseRequestId.current) {
        setExpenseDetails(current => ({ ...current, ...response.data, loading: false }));
      }
    } catch (error) {
      if (requestId === expenseRequestId.current) {
        setExpenseDetails(current => ({ ...current, loading: false, error: 'Impossible de charger les dépenses.' }));
      }
    }
  };
  const closeExpenseDetails = () => {
    expenseRequestId.current += 1;
    setExpenseDetails(current => ({ ...current, open: false }));
  };

  const [paymentDetailsModal, setPaymentDetailsModal] = useState({
    open: false,
    mode: null,
    title: '',
    loading: false,
    payments: [],
    error: null,
  });

  useEffect(() => {
    fetchDashboardData();
    return () => { dashboardRequestId.current += 1; };
  }, [dateRange]);

  const fetchDashboardData = async () => {
    const requestId = ++dashboardRequestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await dashboardAPI.getStats({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      // Debug logging to see what data we're getting
      console.log('Dashboard API Response:', response.data);
      console.log('Sales Trends:', response.data.charts.salesTrends);
      console.log('Top Products:', response.data.charts.topProducts);
      console.log('Date Range:', dateRange);
      
      if (requestId === dashboardRequestId.current) setDashboardData(response.data);
    } catch (e) {
      console.error('Dashboard data fetch error:', e);
      if (requestId === dashboardRequestId.current) setError(e.response?.data?.message || 'Erreur lors du chargement des données du tableau de bord.');
    }
    if (requestId === dashboardRequestId.current) setLoading(false);
  };

  const openPaymentDetails = async (mode, title) => {
    setPaymentDetailsModal({ open: true, mode, title, loading: true, payments: [], error: null });
    try {
      const res = await paymentsAPI.exportPaymentsJson({
        mode,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      setPaymentDetailsModal((prev) => ({
        ...prev,
        loading: false,
        payments: Array.isArray(res.data) ? res.data : [],
      }));
    } catch (e) {
      console.error('Payment details fetch error:', e);
      setPaymentDetailsModal((prev) => ({
        ...prev,
        loading: false,
        error: 'Erreur lors du chargement des paiements.',
      }));
    }
  };

  const closePaymentDetails = () => {
    setPaymentDetailsModal({ open: false, mode: null, title: '', loading: false, payments: [], error: null });
  };

  const paymentDetailsTotal = paymentDetailsModal.payments.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );

  const paymentDetailsColumns = [
    { key: 'payment_date', label: 'Date', render: (v) => formatDateOnlyFr(v) },
    { key: 'client_name', label: 'Client', render: (v) => v || '-' },
    { key: 'reference_type_label', label: 'Type', render: (v, p) => v || p.reference_type || '-' },
    {
      key: 'reference_id',
      label: 'Référence',
      render: (v, p) => {
        const path = p.reference_type === 'sales_order'
          ? `/ventes/commandes/${v}`
          : p.reference_type === 'sale'
            ? `/ventes/${v}`
            : `/ventes/factures/${v}`;
        return (
          <Link to={path} className="text-blue-600 hover:underline font-medium">
            #{v}
          </Link>
        );
      },
    },
    { key: 'amount', label: 'Montant', render: (v) => formatCurrency(v) },
    {
      key: 'cheque_number',
      label: 'N° Chèque',
      render: (v, p) => (p.mode === 'cheque' ? (v || '-') : '-'),
    },
    { key: 'note', label: 'Note', render: (v) => v || '-' },
    ...(user?.role === 'admin'
      ? [{ key: 'store_name', label: 'Magasin', render: (v) => v || '-' }]
      : []),
  ];

  const { keyStats, charts, notifications } = dashboardData;
  const expenseLink = `/depenses?${new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end })}`;
  const expenseColumns = [
    { key: 'expense_date', label: 'Date', render: formatDateOnlyFr },
    { key: 'label', label: 'Dépense' },
    { key: 'category', label: 'Catégorie', render: expenseCategoryLabel },
    { key: 'payment_method', label: 'Mode de paiement', render: expensePaymentLabel },
    { key: 'created_by_name', label: 'Ajoutée par' },
    { key: 'note', label: 'Note', render: value => value || '—' },
    ...(user?.role === 'admin' ? [{ key: 'store_name', label: 'Magasin' }] : []),
    { key: 'amount', label: 'Montant', render: formatCurrency },
  ];

  const clientReferralsTotal = (charts.clientReferrals || []).reduce(
    (sum, cr) => sum + Number(cr.client_count || 0),
    0
  );
  const { paymentMethods } = keyStats;
  
  // Enhanced chart data with better colors and formatting - moved inside render to be reactive
  const getChartData = () => {
    const pieData = {
      labels: ['Payés', 'Partiels', 'Non payés'],
      datasets: [{
        data: [keyStats.payments.paid_count, keyStats.payments.partial_count, keyStats.payments.unpaid_count],
        backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
        borderColor: ['#059669', '#d97706', '#dc2626'],
        borderWidth: 2
      }]
    };

    const paymentTypesData = {
      labels: charts.paymentTypes.map(pt => pt.reference_type),
      datasets: [{
        label: 'Montant par type',
        data: charts.paymentTypes.map(pt => pt.total_amount),
        backgroundColor: [
          '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
          '#ef4444', '#ec4899', '#6366f1', '#f97316', '#84cc16'
        ],
        borderColor: [
          '#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706',
          '#dc2626', '#db2777', '#4f46e5', '#ea580c', '#65a30d'
        ],
        borderWidth: 2
      }]
    };

    const paymentMethodsData = {
      labels: charts.paymentMethods.map(pm => pm.mode_label || pm.mode),
      datasets: [{
        label: 'Montant par mode',
        data: charts.paymentMethods.map(pm => pm.total_amount),
        backgroundColor: [
          '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
          '#06b6d4', '#ec4899', '#6366f1', '#f97316', '#84cc16'
        ],
        borderColor: [
          '#059669', '#2563eb', '#d97706', '#dc2626', '#7c3aed',
          '#0891b2', '#db2777', '#4f46e5', '#ea580c', '#65a30d'
        ],
        borderWidth: 2
      }]
    };

    const salesLineData = {
      labels: charts.salesTrends.map(s => s.date),
      datasets: [{
        label: 'Ventes (MAD)',
        data: charts.salesTrends.map(s => s.total_sales),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59,130,246,0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }]
    };

    const topProductsBar = {
      labels: charts.topProducts.map(p => p.name),
      datasets: [{
        label: 'Quantité vendue',
        data: charts.topProducts.map(p => p.total_sold),
        backgroundColor: [
          '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
          '#ef4444', '#ec4899', '#6366f1', '#f97316', '#84cc16'
        ],
        borderColor: [
          '#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706',
          '#dc2626', '#db2777', '#4f46e5', '#ea580c', '#65a30d'
        ],
        borderWidth: 2
      }]
    };

    const clientGrowthLine = {
      labels: charts.clientGrowth.map(c => c.month),
      datasets: [{
        label: 'Nouveaux clients',
        data: charts.clientGrowth.map(c => c.new_clients),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }]
    };

    const stockByCatBar = {
      labels: charts.stockByCategory.map(c => c.category_name || 'Sans catégorie'),
      datasets: [{
        label: 'Stock par catégorie',
        data: charts.stockByCategory.map(c => c.total_stock),
        backgroundColor: [
          '#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
          '#ef4444', '#ec4899', '#3b82f6', '#f97316', '#84cc16'
        ],
        borderColor: [
          '#4f46e5', '#7c3aed', '#0891b2', '#059669', '#d97706',
          '#dc2626', '#db2777', '#2563eb', '#ea580c', '#65a30d'
        ],
        borderWidth: 2
      }]
    };

    const creditsLine = {
      labels: charts.creditsStats.map(c => c.month),
      datasets: [{
        label: 'Avoirs/Retours',
        data: charts.creditsStats.map(c => c.credits_count),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }]
    };

    const revenueStoreBar = {
      labels: charts.revenueByStore.map(s => s.store_name),
      datasets: [{
        label: 'Chiffre d\'affaires (MAD)',
        data: charts.revenueByStore.map(s => s.total_revenue),
        backgroundColor: [
          '#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6',
          '#06b6d4', '#ec4899', '#6366f1', '#f97316', '#84cc16'
        ],
        borderColor: [
          '#d97706', '#2563eb', '#059669', '#dc2626', '#7c3aed',
          '#0891b2', '#db2777', '#4f46e5', '#ea580c', '#65a30d'
        ],
        borderWidth: 2
      }]
    };

    const empPerfBar = {
      labels: charts.employeePerformance.map(e => e.employee_name),
      datasets: [{
        label: 'Performance (MAD)',
        data: charts.employeePerformance.map(e => e.total_revenue),
        backgroundColor: [
          '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
          '#06b6d4', '#ec4899', '#6366f1', '#f97316', '#84cc16'
        ],
        borderColor: [
          '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed',
          '#0891b2', '#db2777', '#4f46e5', '#ea580c', '#65a30d'
        ],
        borderWidth: 2
      }]
    };

    const referralBg = [
      '#3b82f6', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6',
      '#06b6d4', '#f97316', '#84cc16', '#6366f1', '#ef4444',
      '#14b8a6', '#a855f7', '#64748b'
    ];
    const referralBorder = [
      '#2563eb', '#db2777', '#059669', '#d97706', '#7c3aed',
      '#0891b2', '#ea580c', '#65a30d', '#4f46e5', '#dc2626',
      '#0d9488', '#9333ea', '#475569'
    ];
    const clientReferralData = {
      labels: (charts.clientReferrals || []).map(cr => cr.referral_source),
      datasets: [{
        label: 'Clients par source',
        data: (charts.clientReferrals || []).map(cr => cr.client_count),
        backgroundColor: (charts.clientReferrals || []).map((_, i) => referralBg[i % referralBg.length]),
        borderColor: (charts.clientReferrals || []).map((_, i) => referralBorder[i % referralBorder.length]),
        borderWidth: 2
      }]
    };

    return {
      pieData,
      paymentTypesData,
      paymentMethodsData,
      salesLineData,
      topProductsBar,
      clientGrowthLine,
      stockByCatBar,
      creditsLine,
      revenueStoreBar,
      empPerfBar,
      clientReferralData
    };
  };

  // Get chart data
  const chartData = getChartData();

  // Enhanced chart options for better readability and mobile responsiveness
  const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: true, 
        position: 'bottom', 
        labels: { 
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: 'bold' },
          padding: window.innerWidth < 768 ? 15 : 20,
          usePointStyle: true
        } 
      },
      title: { 
        display: true, 
        font: { size: window.innerWidth < 768 ? 16 : 18, weight: 'bold' },
        padding: { top: 10, bottom: window.innerWidth < 768 ? 15 : 20 }
      },
      tooltip: { 
        enabled: true, 
        mode: 'index', 
        intersect: false,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#3b82f6',
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: { size: window.innerWidth < 768 ? 12 : 14, weight: 'bold' },
        bodyFont: { size: window.innerWidth < 768 ? 11 : 13 }
      },
    },
    layout: { padding: window.innerWidth < 768 ? 15 : 20 },
    elements: { 
      bar: { borderRadius: 8, borderSkipped: false }, 
      point: { radius: window.innerWidth < 768 ? 4 : 6, hoverRadius: window.innerWidth < 768 ? 6 : 8, borderWidth: 2 } 
    },
    scales: {
      x: { 
        grid: { display: true, color: '#e5e7eb' }, 
        ticks: { 
          font: { size: window.innerWidth < 768 ? 10 : 12, weight: 'bold' },
          maxRotation: window.innerWidth < 768 ? 45 : 0
        },
        title: { display: true, font: { size: window.innerWidth < 768 ? 12 : 14, weight: 'bold' } }
      },
      y: { 
        grid: { color: '#e5e7eb' }, 
        ticks: { font: { size: window.innerWidth < 768 ? 10 : 12, weight: 'bold' } },
        title: { display: true, font: { size: window.innerWidth < 768 ? 12 : 14, weight: 'bold' } }
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: true, 
        position: 'bottom', 
        labels: { 
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: 'bold' },
          padding: window.innerWidth < 768 ? 15 : 20,
          usePointStyle: true
        } 
      },
      tooltip: { 
        enabled: true,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#3b82f6',
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: { size: window.innerWidth < 768 ? 12 : 14, weight: 'bold' },
        bodyFont: { size: window.innerWidth < 768 ? 11 : 13 }
      },
    },
    cutout: '60%',
    layout: { padding: window.innerWidth < 768 ? 15 : 20 }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Enhanced Header with Date Range */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-blue-900 tracking-tight mb-3 sm:mb-4">Tableau de bord</h1>
          <p className="text-lg sm:text-xl text-blue-700/80 mb-4 sm:mb-6 px-4">Vue d'ensemble complète des performances commerciales</p>

          <div className="flex justify-center mb-6 sm:mb-8 px-4">
            <Link
              to="/todos"
              className="group w-full max-w-xl rounded-2xl border border-indigo-100 bg-white/90 backdrop-blur-md shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className="flex items-center gap-4 p-4 sm:p-5 bg-gradient-to-r from-indigo-500/10 via-violet-500/10 to-fuchsia-500/10 group-hover:from-indigo-500/15 group-hover:via-violet-500/15 group-hover:to-fuchsia-500/15">
                <div className="flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg">
                  <ListTodo className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Productivité</p>
                  <p className="text-base sm:text-lg font-bold text-slate-900 truncate">Tâches & chronos par magasin</p>
                  <p className="text-xs sm:text-sm text-slate-600 mt-0.5">Assignations, créneaux et durée réelle</p>
                </div>
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400 group-hover:text-indigo-600 shrink-0 transition-colors" />
              </div>
            </Link>
          </div>
          
          {/* Enhanced Date Range Filter - Mobile Responsive */}
          <div className="w-full flex justify-center">
            <ModernCard className="w-full sm:w-auto flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 p-4 sm:p-6 rounded-2xl shadow-lg bg-white/90 backdrop-blur-md border border-blue-100">
              <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  <span className="text-sm sm:text-base font-medium text-blue-700">Période:</span>
                </div>
                <ModernInput
                  type="date"
                  label="Début"
                  value={dateRange.start}
                  onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
                  className="w-full sm:w-40"
                />
                <ModernInput
                  type="date"
                  label="Fin"
                  value={dateRange.end}
                  onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
                  className="w-full sm:w-40"
                />
                <ModernButton
                  onClick={() => setDateRange({ start: getToday(), end: getToday() })}
                  className="w-full sm:w-auto mt-2 sm:mt-0"
                >
                  Aujourd'hui
                </ModernButton>
                <ModernButton
                  onClick={() => setDateRange({ start: '', end: '' })}
                  variant="secondary"
                  className="w-full sm:w-auto mt-2 sm:mt-0"
                >
                  Toutes les données
                </ModernButton>
              </div>
            </ModernCard>
          </div>

          {dashboardData.notifications.overduePayments.length > 0 && (
            <div className="mt-4 sm:mt-6">
              <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full text-sm sm:text-lg font-bold bg-red-100 text-red-800 border-2 border-red-200">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {dashboardData.notifications.overduePayments.length} paiement{dashboardData.notifications.overduePayments.length > 1 ? 's' : ''} en retard
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="text-red-600 text-center font-semibold mb-6 text-base sm:text-lg">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <ModernLoadingSpinner size="lg" />
          </div>
        ) : error ? null : (
          <>
            {/* Enhanced Key Statistics Section - Mobile Responsive Grid */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                <Activity className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Statistiques clés
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
                <ModernStatCard 
                  title="Produits" 
                  value={keyStats.products.total_products} 
                  subtitle={`Stock: ${keyStats.products.total_stock}`}
                  icon={<Package className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />} 
                  variant="primary" 
                  className="rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" 
                />
                <ModernStatCard 
                  title="Clients" 
                  value={keyStats.clients.total_clients} 
                  subtitle="Total clients"
                  icon={<Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />} 
                  variant="success" 
                  className="rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" 
                />
                <ModernStatCard 
                  title="Commandes" 
                  value={keyStats.orders.total_orders} 
                  subtitle="Total commandes"
                  icon={<ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />} 
                  variant="warning" 
                  className="rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" 
                />
                <ModernStatCard 
                  title="Ventes" 
                  value={keyStats.sales.total_sales} 
                  subtitle={`CA: ${formatCurrency(keyStats.sales.total_revenue)}`}
                  icon={<ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />} 
                  variant="danger" 
                  className="rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" 
                />
                <ModernStatCard 
                  title="Paiements" 
                  value={formatCurrency(keyStats.payments.total_amount)} 
                  subtitle={`${keyStats.payments.total_payments} transactions`}
                  icon={<DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />} 
                  variant="primary" 
                  className="rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" 
                />
              </div>
            </div>

            <section className="mb-8 sm:mb-12 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-2xl font-bold text-blue-800">Dépenses et solde de la période</h2>
                <Link to={expenseLink} className="text-primary-600 font-medium hover:underline">Voir / ajouter des dépenses →</Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button type="button" onClick={() => openExpenseDetails()} className="text-left w-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600" aria-label="Voir les dépenses de la période">
                <ModernStatCard title="Dépenses de la période" value={formatCurrency(keyStats.expenses?.total_amount || 0)} subtitle={`${keyStats.expenses?.count || 0} dépenses • période sélectionnée`} icon={<Coins className="text-red-600" />} variant="danger" />
                </button>
                <ModernStatCard title="Total dépenses — toutes dates" value={formatCurrency(keyStats.expenses?.all_time_total || 0)} subtitle={user?.role === 'admin' ? 'Tous les magasins • toutes dates' : 'Votre magasin • toutes dates'} icon={<ClipboardList className="text-orange-600" />} variant="warning" />
                <ModernStatCard title="Solde paiements − dépenses" value={formatCurrency(keyStats.cashFlow?.net || 0)} subtitle="Période sélectionnée • hors solde initial" icon={<DollarSign />} variant={Number(keyStats.cashFlow?.net || 0) < 0 ? 'danger' : 'success'} />
              </div>
              <p className="text-sm text-neutral-500">Solde = paiements enregistrés ({formatCurrency(keyStats.cashFlow?.receipts || 0)}) − dépenses ({formatCurrency(keyStats.expenses?.total_amount || 0)}). Les chèques enregistrés sont inclus, même non encaissés. Ce solde ne représente pas le bénéfice comptable.</p>

            </section>

            {/* Payment Methods Breakdown Section - Mobile Responsive */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                <Coins className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Répartition des moyens de paiement
              </h2>
              
              {!paymentMethods || PAYMENT_MODE_CARDS.every(card => Number(paymentMethods[card.totalKey] || 0) === 0) ? (
                <div className="text-center py-6 sm:py-8">
                  <ModernCard className="p-6 sm:p-8 rounded-2xl shadow-lg bg-gray-50 border border-gray-200">
                    <div className="text-gray-500 text-base sm:text-lg">
                      Aucune donnée de paiement disponible pour cette période
                    </div>
                  </ModernCard>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {PAYMENT_MODE_CARDS.map((card) => {
                    const total = Number(paymentMethods?.[card.totalKey] || 0);
                    const count = Number(paymentMethods?.[card.countKey] || 0);
                    const grandTotal =
                      (Number(paymentMethods?.total_espece) || 0) +
                      (Number(paymentMethods?.total_cheque) || 0) +
                      (Number(paymentMethods?.total_carte) || 0) +
                      (Number(paymentMethods?.total_virement) || 0);
                    const percent = grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : '0.0';
                    const Icon = card.mode === 'cheque' ? ClipboardList : card.mode === 'carte_bancaire' ? CreditCard : DollarSign;

                    return (
                      <button
                        key={card.mode}
                        type="button"
                        onClick={() => count > 0 && openPaymentDetails(card.mode, card.title)}
                        disabled={count === 0}
                        className={`text-left w-full ${count > 0 ? 'cursor-pointer' : 'cursor-default opacity-70'}`}
                        title={count > 0 ? 'Cliquer pour voir le détail' : undefined}
                      >
                        <ModernStatCard
                          title={card.title}
                          value={formatCurrency(total)}
                          subtitle={`${count} transactions • ${percent}%`}
                          icon={<Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${card.iconClass}`} />}
                          variant={card.variant}
                          className={`rounded-2xl shadow-lg transition-all duration-300 transform ${count > 0 ? 'hover:shadow-xl hover:scale-105' : ''}`}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sales & Revenue Analysis Section - Mobile Responsive */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Analyse des ventes et revenus
              </h2>
              
              {/* Fallback Data Notice */}
              {charts.salesTrends.length === 0 && charts.topProducts.length === 0 && (
                <div className="mb-6 text-center">
                  <div className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                    <Info className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">
                      Aucune donnée de vente trouvée pour cette période.
                    </span>
                  </div>
                </div>
              )}
              
              {/* Data Status Debug */}
              <div className="mb-6 text-center">
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-gray-50 text-gray-700 border border-gray-200">
                  <Info className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">
                    Données disponibles: Ventes ({charts.salesTrends?.length || 0}), 
                    Produits ({charts.topProducts?.length || 0}), 
                    Période: {dateRange.start || 'Toutes'} - {dateRange.end || 'Toutes'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Suivez l'évolution quotidienne du chiffre d'affaires pour identifier les tendances et les périodes de forte activité.</p>
                  <div className="h-64 sm:h-80 lg:h-96">
                    {(!charts.salesTrends || charts.salesTrends.length === 0) ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">
                        <div className="text-center">
                          <div className="text-lg font-semibold mb-2">Aucune donnée de ventes</div>
                          <div className="text-sm text-gray-400">Vérifiez la période sélectionnée</div>
                        </div>
                      </div>
                    ) : (
                      <Line 
                        data={chartData.salesLineData} 
                        options={{
                          ...baseChartOptions,
                          plugins: {
                            ...baseChartOptions.plugins,
                            title: { 
                              ...baseChartOptions.plugins.title,
                              display: true, 
                              text: 'Évolution des ventes' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>

                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Découvrez les produits les plus demandés par vos clients pour optimiser votre stock et vos commandes.</p>
                  <div className="h-64 sm:h-80 lg:h-96">
                    {(!charts.topProducts || charts.topProducts.length === 0) ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">
                        <div className="text-center">
                          <div className="text-lg font-semibold mb-2">Aucun produit vendu</div>
                          <div className="text-sm text-gray-400">Vérifiez la période sélectionnée</div>
                        </div>
                      </div>
                    ) : (
                      <Bar 
                        data={chartData.topProductsBar} 
                        options={{
                          ...baseChartOptions,
                          plugins: {
                            ...baseChartOptions.plugins,
                            title: { 
                              ...baseChartOptions.plugins.title,
                              display: true, 
                              text: 'Top 10 produits les plus vendus' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>
              </div>
            </div>

            {/* Payment Analysis Section - Mobile Responsive */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Analyse des paiements
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8">
                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Visualisez le statut de vos paiements : payés, partiels ou en attente pour un meilleur suivi financier.</p>
                  <div className="h-64 sm:h-72 lg:h-80">
                    {keyStats.payments.paid_count + keyStats.payments.partial_count + keyStats.payments.unpaid_count === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucune donnée de paiement</div>
                    ) : (
                      <Pie 
                        data={chartData.pieData} 
                        options={{
                          ...pieOptions,
                          plugins: {
                            ...pieOptions.plugins,
                            title: { 
                              ...pieOptions.plugins.title,
                              display: true, 
                              text: 'Statut des paiements' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>

                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Analysez la répartition des montants entre ventes directes, commandes et factures pour mieux comprendre vos revenus.</p>
                  <div className="h-64 sm:h-72 lg:h-80">
                    {charts.paymentTypes.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucune donnée de type de paiement</div>
                    ) : (
                      <Bar 
                        data={chartData.paymentTypesData} 
                        options={{
                          ...baseChartOptions,
                          plugins: {
                            ...baseChartOptions.plugins,
                            title: { 
                              ...baseChartOptions.plugins.title,
                              display: true, 
                              text: 'Montant par type de paiement' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>

                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100 lg:col-span-2 xl:col-span-1">
                  <p className="text-sm text-gray-600 mb-3 italic">Comparez les méthodes de paiement préférées : espèces, chèques ou cartes bancaires.</p>
                  <div className="h-64 sm:h-72 lg:h-80">
                    {charts.paymentMethods.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucune donnée de mode de paiement</div>
                    ) : (
                      <Doughnut 
                        data={chartData.paymentMethodsData} 
                        options={{
                          ...pieOptions,
                          plugins: {
                            ...pieOptions.plugins,
                            title: { 
                              ...pieOptions.plugins.title,
                              display: true, 
                              text: 'Répartition modes de paiement' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>
              </div>
            </div>

            {/* Business Growth Section - Mobile Responsive */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                <Target className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Croissance et performance
              </h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Mesurez l'acquisition de nouveaux clients pour évaluer l'efficacité de vos actions commerciales et marketing.</p>
                  <div className="h-64 sm:h-80 lg:h-96">
                    {charts.clientGrowth.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucune donnée de nouveaux clients</div>
                    ) : (
                      <Line 
                        data={chartData.clientGrowthLine} 
                        options={{
                          ...baseChartOptions,
                          plugins: {
                            ...baseChartOptions.plugins,
                            title: { 
                              ...baseChartOptions.plugins.title,
                              display: true, 
                              text: 'Évolution des nouveaux clients' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>

                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Visualisez la répartition de votre stock par catégorie pour anticiper vos besoins de réapprovisionnement.</p>
                  <div className="h-64 sm:h-80 lg:h-96">
                    {charts.stockByCategory.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucun stock par catégorie</div>
                    ) : (
                      <Bar 
                        data={chartData.stockByCatBar} 
                        options={{
                          ...baseChartOptions,
                          plugins: {
                            ...baseChartOptions.plugins,
                            title: { 
                              ...baseChartOptions.plugins.title,
                              display: true, 
                              text: 'Stock par catégorie' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>
              </div>
            </div>

            {/* Credits and Returns Section - Mobile Responsive */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Avoirs et retours
              </h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Surveillez l'évolution des avoirs et retours clients pour identifier les problèmes de qualité ou de satisfaction.</p>
                  <div className="h-64 sm:h-80 lg:h-96">
                    {charts.creditsStats.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucune donnée d'avoirs/retours</div>
                    ) : (
                      <Line 
                        data={chartData.creditsLine} 
                        options={{
                          ...baseChartOptions,
                          plugins: {
                            ...baseChartOptions.plugins,
                            title: { 
                              ...baseChartOptions.plugins.title,
                              display: true, 
                              text: 'Évolution des avoirs et retours' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>

                <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-3 italic">Répartition selon « De la part de qui » (fiche client). Les sources non listées et les champs vides sont regroupés sous Autre.</p>
                  <div className="h-64 sm:h-80 lg:h-96">
                    {clientReferralsTotal === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucun client sur la période pour les sources de référence</div>
                    ) : (
                      <Doughnut 
                        data={chartData.clientReferralData} 
                        options={{
                          ...pieOptions,
                          plugins: {
                            ...pieOptions.plugins,
                            title: { 
                              ...pieOptions.plugins.title,
                              display: true, 
                              text: 'Sources de référence clients' 
                            }
                          }
                        }} 
                      />
                    )}
                  </div>
                </ModernCard>
              </div>
            </div>

            {/* Admin Only Section - Mobile Responsive */}
            {user?.role === 'admin' && (
              <div className="mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-4 sm:mb-6 tracking-tight flex items-center justify-center sm:justify-start">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                  Performance par magasin et employés
                </h2>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
                  <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                    <p className="text-sm text-gray-600 mb-3 italic">Comparez les performances de vos différents points de vente pour allouer efficacement vos ressources.</p>
                    <div className="h-64 sm:h-80 lg:h-96">
                      {charts.revenueByStore.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucun chiffre d'affaires par magasin</div>
                      ) : (
                        <Bar 
                          data={chartData.revenueStoreBar} 
                          options={{
                            ...baseChartOptions,
                            plugins: {
                              ...baseChartOptions.plugins,
                              title: { 
                                ...baseChartOptions.plugins.title,
                                display: true, 
                                text: 'Chiffre d\'affaires par magasin' 
                              }
                            }
                          }} 
                        />
                      )}
                    </div>
                  </ModernCard>

                  <ModernCard className="p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 bg-white/95 border border-blue-100">
                    <p className="text-sm text-gray-600 mb-3 italic">Évaluez les contributions individuelles de vos employés pour reconnaître les meilleurs vendeurs et former les autres.</p>
                    <div className="h-64 sm:h-80 lg:h-96">
                      {charts.employeePerformance.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500 text-base sm:text-lg">Aucune donnée de performance employé</div>
                      ) : (
                        <Bar 
                          data={chartData.empPerfBar} 
                          options={{
                            ...baseChartOptions,
                            plugins: {
                              ...baseChartOptions.plugins,
                              title: { 
                                ...baseChartOptions.plugins.title,
                                display: true, 
                                text: 'Performance des employés' 
                              }
                            }
                          }} 
                        />
                      )}
                    </div>
                  </ModernCard>
                </div>
              </div>
            )}

            {/* Notifications Section - Mobile Responsive */}
            <ModernCard className="p-4 sm:p-6 lg:p-8 mt-8 sm:mt-12 rounded-2xl shadow-lg bg-white/95 border border-blue-100">
              <h2 className="text-xl sm:text-2xl font-bold text-blue-800 mb-4 sm:mb-6 flex items-center justify-center sm:justify-start">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-red-600" />
                Notifications Paiements
              </h2>
              {notifications.overduePayments.length === 0 ? (
                <div className="text-blue-700/70 text-base sm:text-lg text-center sm:text-left">Aucun paiement en retard ou incomplet.</div>
              ) : (
                <div className="space-y-3">
                  {notifications.overduePayments.map(p => (
                    <div key={p.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200 space-y-2 sm:space-y-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
                        <ModernBadge variant="danger">En retard</ModernBadge>
                        <span className="font-semibold text-sm sm:text-base">Paiement #{p.id}</span>
                        <span className="text-gray-600 text-sm sm:text-base">({p.reference_type_label} #{p.reference_id})</span>
                        <span className="font-bold text-red-600 text-sm sm:text-base">{formatCurrency(p.amount)}</span>
                      </div>
                      <span className="text-xs sm:text-sm text-gray-500">{p.payment_date}</span>
                    </div>
                  ))}
                </div>
              )}
            </ModernCard>
          </>
        )}
      </div>

      <ModernModal
        isOpen={expenseDetails.open}
        onClose={closeExpenseDetails}
        title="Dépenses de la période"
        size="xxl"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <span className="text-sm text-neutral-600">Période : {expenseDetails.range.start ? formatDateOnlyFr(expenseDetails.range.start) : 'Toutes dates'} — {expenseDetails.range.end ? formatDateOnlyFr(expenseDetails.range.end) : 'Toutes dates'}</span>
            {!expenseDetails.loading && !expenseDetails.error && expenseDetails.summary && <span className="font-bold">{expenseDetails.summary.count} dépenses • Total : {formatCurrency(expenseDetails.summary.total_amount)}</span>}
            <ModernButton variant="ghost" onClick={closeExpenseDetails}>Fermer</ModernButton>
          </div>
        }
      >
        {expenseDetails.loading ? <div className="flex justify-center py-12"><ModernLoadingSpinner /></div> : expenseDetails.error ? (
          <div className="text-center py-8"><p role="alert" className="text-red-600 mb-4">{expenseDetails.error}</p><ModernButton onClick={() => openExpenseDetails(expenseDetails.page, expenseDetails.range)}>Réessayer</ModernButton></div>
        ) : <>
          {expenseDetails.rows.length ? <ModernTable columns={expenseColumns} data={expenseDetails.rows} /> : <p className="text-center py-8 text-neutral-500">Aucune dépense pour cette période.</p>}
          {Number(expenseDetails.summary?.count) > expenseDetails.pageSize && <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <span className="text-sm text-neutral-500">Page {expenseDetails.page} sur {Math.ceil(Number(expenseDetails.summary.count) / expenseDetails.pageSize)}</span>
            <div className="flex gap-2">
              <ModernButton variant="secondary" disabled={expenseDetails.page <= 1} onClick={() => openExpenseDetails(expenseDetails.page - 1, expenseDetails.range)}>Précédent</ModernButton>
              <ModernButton variant="secondary" disabled={expenseDetails.page * expenseDetails.pageSize >= Number(expenseDetails.summary.count)} onClick={() => openExpenseDetails(expenseDetails.page + 1, expenseDetails.range)}>Suivant</ModernButton>
            </div>
          </div>}
        </>}
      </ModernModal>

      <ModernModal
        isOpen={paymentDetailsModal.open}
        onClose={closePaymentDetails}
        title={`Détail des paiements — ${paymentDetailsModal.title}`}
        size="xxl"
        footer={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
            <div className="text-sm text-neutral-600">
              Période : {formatDateOnlyFr(dateRange.start)} — {formatDateOnlyFr(dateRange.end)}
              {' • '}
              {paymentDetailsModal.payments.length} transaction{paymentDetailsModal.payments.length !== 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-neutral-900">
                Total : {formatCurrency(paymentDetailsTotal)}
              </span>
              <ModernButton variant="ghost" onClick={closePaymentDetails}>
                Fermer
              </ModernButton>
            </div>
          </div>
        }
      >
        {paymentDetailsModal.loading ? (
          <div className="flex justify-center py-12">
            <ModernLoadingSpinner />
          </div>
        ) : paymentDetailsModal.error ? (
          <div className="text-center py-8 text-red-600">{paymentDetailsModal.error}</div>
        ) : paymentDetailsModal.payments.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            Aucun paiement {MODE_LABELS[paymentDetailsModal.mode]?.toLowerCase() || ''} pour cette période.
          </div>
        ) : (
          <ModernTable columns={paymentDetailsColumns} data={paymentDetailsModal.payments} />
        )}
      </ModernModal>
    </div>
  );
};

export default Dashboard; 